import { motion, AnimatePresence, cubicBezier } from 'motion/react'
import { useState } from 'react'
import {
	PracticeSet,
	PracticeSetAnswer,
	PracticeSetItem,
	PracticeSetItemChoice,
	PracticeSetItemSentence,
	PracticeSetItemVocabulary,
} from '@/models'
import { cn } from '@/lib/utils'
import PracticeOverview from '@/components/practice-overview'
import AnkiPractice from '@/components/anki-practice'
import useGlobalStore from '@/useGlobalStore.ts'
import { useBus } from '@/useBus.ts'
import { Loader2 } from 'lucide-react'
import SentencesPractice from '@/components/sentences-practice.tsx'
import ChoicesPractice from '@/components/choices-practice.tsx'

const PagePractice = ({ className }: { className: string }) => {
	const {
		loadLibraryOverview,
		loadPracticeOverview,
		isPracticeOverviewLoading,
		currentPracticeStatus,
		setCurrentPracticeStatus,
	} = useGlobalStore()
	const [showPracticeOverview, setShowPracticeOverview] = useState(true)
	const [practiceSet, setPracticeSet] = useState<PracticeSet>()
	const [practiceItems, setPracticeItems] = useState<PracticeSetItem[]>([])
	const [practiceAnswers, setPracticeAnswers] = useState<PracticeSetAnswer | null>(null)

	const fadeVariants = {
		hidden: { opacity: 0 },
		visible: { opacity: 1 },
		exit: { opacity: 0 },
	}

	const onPracticeStarted = async (
		practiceSet: PracticeSet,
		items: PracticeSetItem[],
		answers: PracticeSetAnswer | null = null,
	) => {
		setPracticeAnswers(answers)
		setPracticeItems(items)
		setPracticeSet(practiceSet)
		setShowPracticeOverview(false)
		setCurrentPracticeStatus('started')
	}

	// const cancelPractice = async () => {
	// 	await api('/practice/sets/end?reason=cancelled', {
	// 		method: 'POST',
	// 	})
	// }

	useBus('end-current-practice', () => {
		onPracticeFinished().then()
		onClosePractice().then()
	})

	useBus(
		'bottom-tab-pressed',
		(tabNumber) => {
			if (tabNumber === 2 && currentPracticeStatus === 'finished') {
				onClosePractice().then()
			}
		},
		[currentPracticeStatus],
	)

	const onPracticeFinished = async () => {
		setCurrentPracticeStatus('finished')
		await loadLibraryOverview()
		await loadPracticeOverview()
	}

	const onClosePractice = async () => {
		setShowPracticeOverview(false)
		setPracticeSet(undefined)
		await loadLibraryOverview()
	}

	useBus('reload-open-collection', async () => {
		await loadPracticeOverview()
	})

	return (
		<>
			<AnimatePresence>
				{!showPracticeOverview && practiceSet !== undefined ? (
					practiceSet.type === 'anki' || practiceSet.type === 'vocabulary' /* vocabulary is deprecated */ ? (
						<motion.div
							className={cn(
								className,
								`${currentPracticeStatus !== 'finished' ? 'overflow-y-hidden' : ''} overflow-x-hidden flex-col gap-5`,
							)}
							key="AnkiPractice"
							initial="hidden"
							animate="visible"
							exit="exit"
							variants={fadeVariants}
							transition={{ duration: 0.5, ease: cubicBezier(0.25, 0.1, 0.25, 1) }}>
							<AnkiPractice
								practiceSet={practiceSet}
								practiceSetItems={practiceItems as PracticeSetItemVocabulary[]}
								onPracticeAnswersSubmitted={onPracticeFinished}
								onClosePracticeSet={() => onClosePractice()}
								answerResultCompleted={practiceAnswers}
							/>
						</motion.div>
					) : practiceSet.type === 'sentences' ? (
						<motion.div
							className={cn(className, 'overflow-x-hidden flex-col gap-5 h-full')}
							key="SentencesPractice">
							<SentencesPractice
								practiceSet={practiceSet}
								practiceSetItems={practiceItems as PracticeSetItemSentence[]}
								onPracticeAnswersSubmitted={onPracticeFinished}
								onClosePracticeSet={() => onClosePractice()}
								answerResultCompleted={practiceAnswers}
							/>
						</motion.div>
					) : practiceSet.type === 'choices' ? (
						<motion.div
							className={cn(className, 'overflow-x-hidden flex-col gap-5 h-full')}
							key="ChoicesPractice">
							<ChoicesPractice
								practiceSet={practiceSet}
								practiceSetItems={practiceItems as PracticeSetItemChoice[]}
								onPracticeAnswersSubmitted={onPracticeFinished}
								onClosePracticeSet={() => onClosePractice()}
								answerResultCompleted={practiceAnswers}
							/>
						</motion.div>
					) : (
						<div>-</div>
					)
				) : (
					<motion.div
						className={cn(className, 'overflow-x-hidden flex-col gap-3')}
						key="PracticeOverview"
						initial="hidden"
						animate="visible"
						exit="exit"
						variants={fadeVariants}
						transition={{ duration: 0.5, ease: cubicBezier(0.25, 0.1, 0.25, 1) }}>
						{isPracticeOverviewLoading ? (
							<Loader2 className="w-9 h-9 animate-spin self-center m-auto" />
						) : (
							<PracticeOverview onPracticeLoaded={onPracticeStarted} />
						)}
					</motion.div>
				)}
			</AnimatePresence>
		</>
	)
}

export default PagePractice
